import * as Sentry from '@sentry/react';
import {
  GraphQLClient as GraphQLRequestClient,
  RequestDocument,
  Variables,
} from 'graphql-request';
import {
  GraphQLClientRequestHeaders,
  RequestOptions,
} from 'graphql-request/build/esm/types';
import { AuthenticationError, GraphqlError } from 'src/types/errors';

// Default timeout for GraphQL requests (30 seconds)
const DEFAULT_REQUEST_TIMEOUT = 30000;

/** Network-level failures we don't report to Sentry (user offline, timeout, DNS, etc.). */
function isNetworkFailure(error: unknown): boolean {
  if (error instanceof Error) {
    if (error.name === 'AbortError') return true;
    if (error.name === 'TypeError' && error.message === 'Failed to fetch')
      return true;
    // Some environments use a different message
    if (
      error.message?.includes('fetch') &&
      /network|failed|load|connection/i.test(error.message)
    )
      return true;
  }
  return false;
}

/** 401 Unauthorized - we handle by redirecting to login; don't report to Sentry. */
function isAuthError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return status === 401;
}

export class GraphqlClient {
  private constructor(
    public url: string,
    private getAccessToken: () => Promise<string>,
    private readonly _graphqlRequestClient: GraphQLRequestClient,
  ) {}

  private static defaultHeaders: GraphQLClientRequestHeaders = {};

  static setDefaultHeaders(headers: GraphQLClientRequestHeaders) {
    this.defaultHeaders = headers;
  }

  static initialize(
    url: string,
    getAccessToken: () => Promise<string>,
    graphqlRequestClient: GraphQLRequestClient,
  ) {
    // Maintains the singleton instance of GraphqlClient.
    if (!this.instance) {
      this.instance = new GraphqlClient(
        url,
        getAccessToken,
        graphqlRequestClient,
      );
    }
  }

  public static instance: GraphqlClient | undefined;

  async request<T>(
    document: RequestDocument,
    variables?: Variables,
    requestHeaders?: GraphQLClientRequestHeaders,
    signal?: AbortSignal,
  ): Promise<T> {
    const timeoutAbortController = new AbortController();
    let token: string;

    try {
      token = await this.getAccessToken();
    } catch {
      throw new AuthenticationError();
    }

    // Set up timeout to abort request after DEFAULT_REQUEST_TIMEOUT
    const timeoutId = setTimeout(() => {
      timeoutAbortController.abort();
    }, DEFAULT_REQUEST_TIMEOUT);

    // Combine external signal with timeout signal
    // If external signal is provided, abort timeout controller when external signal aborts
    if (signal) {
      signal.addEventListener('abort', () => {
        timeoutAbortController.abort();
        clearTimeout(timeoutId);
      });
    }

    // Use external signal if provided, otherwise use timeout signal
    const combinedSignal = signal || timeoutAbortController.signal;

    try {
      const requestOptions: RequestOptions = {
        document,
        variables,
        requestHeaders: {
          Authorization: `Bearer ${token}`,
          ...requestHeaders,
          ...GraphqlClient.defaultHeaders,
        },
        signal: combinedSignal,
      };

      return await this._graphqlRequestClient.request<T>(requestOptions);
    } catch (error: unknown) {
      clearTimeout(timeoutId);
      // Don't report expected/handled errors to Sentry (network, 401 → redirect to login)
      if (!isNetworkFailure(error) && !isAuthError(error)) {
        Sentry.captureException(error);
      }

      // Check if error is due to timeout/abort or other network failure
      if (error instanceof Error && error.name === 'AbortError') {
        throw {
          message: 'Request timeout - network may be unavailable',
        };
      } else if (isNetworkFailure(error)) {
        throw {
          message: 'Network error - please check your connection and try again',
        };
      }

      const reqRes = JSON.parse(JSON.stringify(error));
      const status = reqRes.response?.status;

      if (status === 401) {
        throw new AuthenticationError();
      }

      if (reqRes?.response?.errors) {
        const message = reqRes.response?.errors[0]?.message;
        const code = reqRes.response?.errors[0]?.extensions?.code;

        if (status == 200) {
          if (code) {
            throw new GraphqlError(message, code);
          }
        }

        throw { message: reqRes.response.errors[0].message };
      }

      throw { message: 'Unknown error' };
    } finally {
      clearTimeout(timeoutId);
    }
  }
}
